// src/HomePage.js
// Home screen — shown after login before entering the builder.
// Displays agreement stats, pending agreements prominently,
// all other agreements below, and a Create New Agreement button.

import { useState, useEffect } from "react";

const STATUS_LABELS = {
  building:          "In Progress",
  pending_review:    "Pending Approval",
  approved:          "Approved",
  sent:              "Sent to Client",
  client_commented:  "Client Has Comments",
  client_signed:     "Awaiting Jeff's Signature",
  signed:            "Fully Signed",
};

const STATUS_COLORS = {
  building:          { bg: "#F3E5F5", text: "#6A1B9A",  dot: "#AB47BC" },
  pending_review:    { bg: "#FFF9C4", text: "#795548",  dot: "#F9A825" },
  approved:          { bg: "#E8F5E9", text: "#2e7d32",  dot: "#2e7d32" },
  sent:              { bg: "#E3F2FD", text: "#1565C0",  dot: "#1565C0" },
  client_commented:  { bg: "#FFF3E0", text: "#b45309",  dot: "#f59e0b" },
  client_signed:     { bg: "#FFF3E0", text: "#E65100",  dot: "#FF6D00" },
  signed:            { bg: "#E1F5EE", text: "#1D9E75",  dot: "#1D9E75" },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: "#f4f5f7", text: "#666", dot: "#999" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: c.bg, color: c.text,
      fontSize: 12, fontWeight: 500,
      padding: "3px 10px", borderRadius: 20,
      whiteSpace: "nowrap",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 60)   return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days < 30)   return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const PAGE_SIZE = 15;

export default function HomePage({ user, onCreateNew, onOpenDraft, onOverrideSend, onViewComments, onTestPanel, onChangelog }) {
  const [drafts, setDrafts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [deletingToken, setDeletingToken] = useState(null);
  const [search, setSearch]   = useState("");
  const [page, setPage]       = useState(0);

  useEffect(() => {
    fetch("/api/drafts")
      .then(r => { if (!r.ok) throw new Error("Failed to load"); return r.json(); })
      .then(data => { setDrafts(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  // Allow page-level scrolling on the home screen (body/root are locked for the builder)
  useEffect(() => {
    document.body.style.overflow   = "auto";
    document.body.style.height     = "auto";
    document.getElementById("root").style.height   = "auto";
    document.getElementById("root").style.overflow = "visible";
    return () => {
      document.body.style.overflow   = "";
      document.body.style.height     = "";
      document.getElementById("root").style.height   = "";
      document.getElementById("root").style.overflow = "";
    };
  }, []);

  async function handleDelete(token) {
    if (!window.confirm("Remove this agreement? This cannot be undone.")) return;
    setDeletingToken(token);
    try {
      await fetch(`/api/draft/${token}`, { method: "DELETE" });
      setDrafts(prev => prev.filter(d => d.token !== token));
    } catch {
      alert("Failed to delete agreement.");
    } finally {
      setDeletingToken(null);
    }
  }

  // Split into pending (needs action) and everything else
  const pending = drafts.filter(d => ["pending_review", "client_commented", "client_signed"].includes(d.status));

  const q = search.trim().toLowerCase();
  const filteredOthers = drafts
    .filter(d => !["pending_review", "client_commented", "client_signed"].includes(d.status))
    .filter(d => {
      if (!q) return true;
      return (
        (d.customerName || "").toLowerCase().includes(q) ||
        (d.createdBy    || "").toLowerCase().includes(q) ||
        (STATUS_LABELS[d.status] || "").toLowerCase().includes(q) ||
        (d.products || []).some(p => p.toLowerCase().includes(q))
      );
    });

  const totalPages  = Math.ceil(filteredOthers.length / PAGE_SIZE);
  const pagedOthers = filteredOthers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const others = filteredOthers; // kept for empty-state check

  // Stats
  const stats = [
    { label: "Total",           value: drafts.length,                                                                           color: "#1a3a6e" },
    { label: "In Progress",     value: drafts.filter(d => d.status === "building").length,                                     color: "#AB47BC" },
    { label: "Pending Approval",value: drafts.filter(d => d.status === "pending_review").length,                               color: "#F9A825" },
    { label: "Sent to Client",  value: drafts.filter(d => d.status === "sent").length,                                        color: "#1565C0" },
    { label: "Awaiting Jeff",   value: drafts.filter(d => d.status === "client_signed").length,                                color: "#FF6D00" },
    { label: "Signed",          value: drafts.filter(d => d.status === "signed").length,                                       color: "#1D9E75" },
  ];

  // What action button to show per draft
  function actionButton(draft) {
    const base = {
      padding: "5px 14px", borderRadius: 6, fontSize: 12, fontWeight: 500,
      cursor: "pointer", border: "none", whiteSpace: "nowrap",
    };
    if (draft.status === "building") {
      return (
        <button onClick={() => onOpenDraft(draft)} style={{ ...base, background: "#6A1B9A", color: "#fff" }}>
          Continue Editing →
        </button>
      );
    }
    if (draft.status === "pending_review") {
      const approvalCount = Object.keys(draft.approvals || {}).length;
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
          <button onClick={() => onOpenDraft(draft)} style={{ ...base, background: "#1a3a6e", color: "#fff" }}>
            Review →
          </button>
          {approvalCount >= 1 && onOverrideSend && (
            <button
              onClick={() => onOverrideSend(draft)}
              title={`${approvalCount}/2 boss${approvalCount !== 1 ? "es" : ""} approved — click to override and send now`}
              style={{ ...base, background: "#fff8e1", color: "#b45309", border: "1px solid #fcd34d", fontSize: 11 }}
            >
              Override & Send →
            </button>
          )}
        </div>
      );
    }
    if (draft.status === "approved") {
      return (
        <button onClick={() => onOpenDraft(draft)} style={{ ...base, background: "#1D9E75", color: "#fff" }}>
          Send to Client →
        </button>
      );
    }
    if (draft.status === "sent") {
      return (
        <button onClick={() => onOpenDraft(draft)} style={{ ...base, background: "#E3F2FD", color: "#1565C0" }}>
          View
        </button>
      );
    }
    if (draft.status === "client_commented") {
      const count = (draft.clientComments || []).filter(c => !c.teamResponse).length;
      return (
        <button
          onClick={() => onViewComments && onViewComments(draft)}
          title={`${count} comment${count !== 1 ? "s" : ""} awaiting response`}
          style={{ ...base, background: "#b45309", color: "#fff" }}
        >
          View Comments ({count}) →
        </button>
      );
    }
    if (draft.status === "client_signed") {
      return (
        <button onClick={() => onOpenDraft(draft)} style={{ ...base, background: "#FFF3E0", color: "#E65100", border: "1px solid #ffb347" }}>
          View Status →
        </button>
      );
    }
    if (draft.status === "signed") {
      return (
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => onOpenDraft(draft)} style={{ ...base, background: "#E1F5EE", color: "#1D9E75" }}>
            View
          </button>
          <a
            href={`/api/draft/${draft.token}/pdf`}
            download
            style={{ ...base, background: "#fff", color: "#1D9E75", border: "1px solid #1D9E75", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            ⬇ PDF
          </a>
        </div>
      );
    }
    return null;
  }

  function DraftRow({ draft, highlight }) {
    return (
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 185px 120px 90px 150px 36px",
        gap: 12,
        alignItems: "center",
        padding: "14px 20px",
        background: highlight ? "#FFFDF0" : "#fff",
        borderBottom: "0.5px solid #eee",
        transition: "background 0.15s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "#f9f9f9"}
      onMouseLeave={e => e.currentTarget.style.background = highlight ? "#FFFDF0" : "#fff"}
      >
        {/* Customer name + products */}
        <div>
          <div style={{ fontWeight: 500, fontSize: 14, color: "#1a1a2e", marginBottom: 2 }}>
            {draft.customerName || "Unnamed"}
          </div>
          <div style={{ fontSize: 12, color: "#999" }}>
            {(draft.products || []).slice(0, 3).join(", ")}
            {(draft.products || []).length > 3 && ` +${draft.products.length - 3} more`}
          </div>
        </div>

        {/* Status */}
        <div><StatusBadge status={draft.status} /></div>

        {/* Created by */}
        <div style={{ fontSize: 12, color: "#888" }}>{draft.createdBy || "—"}</div>

        {/* Time */}
        <div style={{ fontSize: 12, color: "#aaa" }}>{timeAgo(draft.updatedAt || draft.createdAt)}</div>

        {/* Action */}
        <div style={{ textAlign: "right" }}>{actionButton(draft)}</div>

        {/* Delete */}
        <div style={{ textAlign: "right" }}>
          <button
            onClick={() => handleDelete(draft.token)}
            disabled={deletingToken === draft.token}
            title="Remove agreement"
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#ccc", fontSize: 16, padding: "2px 4px", lineHeight: 1,
              borderRadius: 4,
            }}
            onMouseEnter={e => e.currentTarget.style.color = "#993C1D"}
            onMouseLeave={e => e.currentTarget.style.color = "#ccc"}
          >
            {deletingToken === draft.token ? "…" : "✕"}
          </button>
        </div>
      </div>
    );
  }

  // ── Countersigning section (admin only) ─────────────────────────────────────
  // Controls who receives the "please countersign" email once a client signs.
  function CountersignSection() {
    const [data,   setData]   = useState(null);
    const [open,   setOpen]   = useState(false);
    const [form,   setForm]   = useState({ countersignerEmail: "", countersignerName: "", countersignerTitle: "" });
    const [saving, setSaving] = useState(false);
    const [err,    setErr]    = useState("");
    const [saved,  setSaved]  = useState(false);

    const load = async () => {
      try {
        const r = await fetch("/api/settings");
        const d = await r.json();
        setData(d);
        setForm({
          countersignerEmail: d.countersigner?.email || "",
          countersignerName:  d.countersigner?.name  || "",
          countersignerTitle: d.countersigner?.title || "",
          hubspotSowPropertyName: d.hubspot?.sowPropertyName || "",
          hubspotStageOnSent:     d.hubspot?.stageOnSent     || "",
          hubspotStageOnClientSigned: d.hubspot?.stageOnClientSigned || "",
          hubspotStageOnSigned:   d.hubspot?.stageOnSigned   || "",
        });
      } catch { setData({}); }
    };

    const handleOpen = () => { setOpen(o => !o); if (!data) load(); };

    const save = async () => {
      setErr(""); setSaved(false); setSaving(true);
      try {
        const r = await fetch("/api/settings", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
        const d = await r.json();
        if (!r.ok) { setErr(d.error || "Failed to save"); return; }
        setData(d); setSaved(true); setTimeout(() => setSaved(false), 2500);
      } catch { setErr("Could not reach the server."); }
      finally { setSaving(false); }
    };

    const inputStyle = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, width: "100%", boxSizing: "border-box" };

    return (
      <div style={{ marginTop: 16 }}>
        <button onClick={handleOpen} style={{ background: "none", border: "1px solid #ddd", borderRadius: 8, padding: "8px 16px", fontSize: 13, color: "#666", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
          <span>✍️</span> Countersigning {open ? "▲" : "▼"}
        </button>

        {open && (
          <div style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", marginTop: 10, overflow: "hidden" }}>
            <div style={{ background: "#1a3a6e", color: "#fff", padding: "10px 20px" }}>
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Countersigner</span>
            </div>
            <div style={{ padding: "16px 20px" }}>
              <div style={{ fontSize: 12, color: "#666", lineHeight: 1.6, marginBottom: 14 }}>
                When a client finishes signing, the request to countersign is emailed here.
                This name and title also appear on the Dynamic Health IT side of the signature block.
              </div>
              {err   && <div style={{ color: "#c62828", fontSize: 12, marginBottom: 10 }}>{err}</div>}
              {saved && <div style={{ color: "#1a6e3c", fontSize: 12, marginBottom: 10 }}>✓ Saved — new agreements will route here.</div>}
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 4 }}>EMAIL</div>
                  <input value={form.countersignerEmail} onChange={e => setForm(f => ({ ...f, countersignerEmail: e.target.value }))} placeholder="jrobbins@dynamichealthit.com" style={inputStyle} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 4 }}>NAME</div>
                  <input value={form.countersignerName} onChange={e => setForm(f => ({ ...f, countersignerName: e.target.value }))} placeholder="Jeffery P. Robbins" style={inputStyle} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 4 }}>TITLE</div>
                  <input value={form.countersignerTitle} onChange={e => setForm(f => ({ ...f, countersignerTitle: e.target.value }))} placeholder="President" style={inputStyle} />
                </div>
                <button onClick={save} disabled={saving} style={{ background: "#1a3a6e", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1 }}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
              {data && !data.countersignerIsCustom && (
                <div style={{ fontSize: 11, color: "#999", marginTop: 10 }}>
                  Currently using the default ({data.defaults?.email}). Save to override.
                </div>
              )}
              <div style={{ fontSize: 11, color: "#999", marginTop: 10 }}>
                Changing this only affects agreements signed from now on — already-executed PDFs keep the name they were countersigned with.
              </div>

              <div style={{ borderTop: "1px solid #eee", margin: "16px 0 0", paddingTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#1a3a6e", marginBottom: 6 }}>HubSpot deal writes</div>
                <div style={{ fontSize: 12, color: "#666", lineHeight: 1.6, marginBottom: 12 }}>
                  The SOW Name is written to the linked deal when an agreement is sent, and deals in the
                  <strong> New Sales Pipeline</strong> advance automatically: <strong>Contract Sent</strong> → <strong>Client Signed</strong> → <strong>Fully Executed</strong>.
                  Deals in any other pipeline (e.g. Renewal) are left where they are. Leave these blank
                  unless you need to override a stage; find IDs with <code style={{ background: "#f4f5f7", padding: "1px 4px", borderRadius: 3 }}>node tools/hubspot-inspect.js</code>.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 4 }}>SOW PROPERTY (INTERNAL NAME)</div>
                    <input value={form.hubspotSowPropertyName || ""} onChange={e => setForm(f => ({ ...f, hubspotSowPropertyName: e.target.value }))} placeholder="auto-detect by label" style={inputStyle} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 4 }}>STAGE ON SENT</div>
                    <input value={form.hubspotStageOnSent || ""} onChange={e => setForm(f => ({ ...f, hubspotStageOnSent: e.target.value }))} placeholder="auto (Contract Sent)" style={inputStyle} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 4 }}>STAGE ON CLIENT SIGNED</div>
                    <input value={form.hubspotStageOnClientSigned || ""} onChange={e => setForm(f => ({ ...f, hubspotStageOnClientSigned: e.target.value }))} placeholder="auto (Client Signed)" style={inputStyle} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 4 }}>STAGE ON COUNTERSIGNED</div>
                    <input value={form.hubspotStageOnSigned || ""} onChange={e => setForm(f => ({ ...f, hubspotStageOnSigned: e.target.value }))} placeholder="auto (Fully Executed)" style={inputStyle} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Team Members section (admin only) ───────────────────────────────────────
  function TeamSection() {
    const [users,       setUsers]       = useState(null);
    const [open,        setOpen]        = useState(false);
    const [addOpen,     setAddOpen]     = useState(false);
    const [editId,      setEditId]      = useState(null);
    const [form,        setForm]        = useState({ name: "", role: "user", password: "" });
    const [editForm,    setEditForm]    = useState({});
    const [saving,      setSaving]      = useState(false);
    const [deleting,    setDeleting]    = useState(null);
    const [err,         setErr]         = useState("");

    const loadTeam = async () => {
      try { const r = await fetch("/api/users"); setUsers(await r.json()); }
      catch { setUsers([]); }
    };

    const handleOpen = () => { setOpen(o => !o); if (!users) loadTeam(); };

    const handleAdd = async () => {
      setErr(""); setSaving(true);
      try {
        const r = await fetch("/api/users", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
        const d = await r.json();
        if (!r.ok) { setErr(d.error || "Failed"); return; }
        setUsers(u => [...u, d]);
        setForm({ name: "", role: "user", password: "" });
        setAddOpen(false);
      } catch { setErr("Request failed"); }
      finally { setSaving(false); }
    };

    const handleEdit = async (id) => {
      setErr(""); setSaving(true);
      try {
        const r = await fetch(`/api/users/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(editForm) });
        const d = await r.json();
        if (!r.ok) { setErr(d.error || "Failed"); return; }
        setUsers(u => u.map(x => x.id === id ? d : x));
        setEditId(null);
      } catch { setErr("Request failed"); }
      finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
      if (!window.confirm("Remove this user? They will not be able to log in.")) return;
      setDeleting(id);
      try {
        await fetch(`/api/users/${id}`, { method: "DELETE" });
        setUsers(u => u.filter(x => x.id !== id));
      } catch { alert("Failed to delete user"); }
      finally { setDeleting(null); }
    };

    const inputStyle = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, width: "100%", boxSizing: "border-box" };
    const btnStyle = (color) => ({ background: color, color: "#fff", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" });

    return (
      <div style={{ marginTop: 32 }}>
        <button onClick={handleOpen} style={{ background: "none", border: "1px solid #ddd", borderRadius: 8, padding: "8px 16px", fontSize: 13, color: "#666", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
          <span>👥</span> Team Members {open ? "▲" : "▼"}
        </button>

        {open && (
          <div style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", marginTop: 10, overflow: "hidden" }}>
            {/* Header */}
            <div style={{ background: "#1a3a6e", color: "#fff", padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>Team Members</span>
              <button onClick={() => { setAddOpen(o => !o); setErr(""); }} style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>
                {addOpen ? "Cancel" : "+ Add User"}
              </button>
            </div>

            {/* Add user form */}
            {addOpen && (
              <div style={{ padding: "16px 20px", background: "#f9f9fb", borderBottom: "0.5px solid #eee" }}>
                {err && <div style={{ color: "#c62828", fontSize: 12, marginBottom: 10 }}>{err}</div>}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 1fr auto", gap: 10, alignItems: "end" }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 4 }}>FULL NAME</div>
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Jordan Smith" style={inputStyle} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 4 }}>ROLE</div>
                    <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={{ ...inputStyle, width: "100%" }}>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 4 }}>PASSWORD</div>
                    <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Set a password" style={inputStyle} />
                  </div>
                  <button onClick={handleAdd} disabled={saving || !form.name || !form.password} style={btnStyle("#1a3a6e")}>
                    {saving ? "Saving…" : "Add"}
                  </button>
                </div>
              </div>
            )}

            {/* User list */}
            {users === null ? (
              <div style={{ padding: "20px", color: "#aaa", fontSize: 13 }}>Loading…</div>
            ) : users.length === 0 ? (
              <div style={{ padding: "20px", color: "#aaa", fontSize: 13 }}>No users found.</div>
            ) : (
              users.map(u => (
                <div key={u.id} style={{ borderBottom: "0.5px solid #eee", padding: "12px 20px" }}>
                  {editId === u.id ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 1fr auto auto", gap: 10, alignItems: "end" }}>
                      {err && <div style={{ color: "#c62828", fontSize: 12, gridColumn: "1/-1", marginBottom: 4 }}>{err}</div>}
                      <div>
                        <input defaultValue={u.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
                      </div>
                      <div>
                        <select defaultValue={u.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} style={{ ...inputStyle, width: "100%" }}>
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <div>
                        <input type="password" placeholder="New password (leave blank to keep)" onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))} style={inputStyle} />
                      </div>
                      <button onClick={() => handleEdit(u.id)} disabled={saving} style={btnStyle("#1a3a6e")}>{saving ? "…" : "Save"}</button>
                      <button onClick={() => { setEditId(null); setErr(""); }} style={{ ...btnStyle("#999"), background: "#f4f5f7", color: "#666" }}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e", flex: 1 }}>{u.name}</div>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20,
                        background: u.role === "admin" ? "#E8EAF6" : "#f4f5f7",
                        color: u.role === "admin" ? "#3949AB" : "#666",
                      }}>{u.role}</span>
                      <div style={{ fontSize: 11, color: "#bbb" }}>
                        Added {u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </div>
                      <button onClick={() => { setEditId(u.id); setEditForm({}); setErr(""); }} style={{ background: "none", border: "1px solid #ddd", borderRadius: 6, color: "#666", fontSize: 12, padding: "4px 10px", cursor: "pointer" }}>Edit</button>
                      <button onClick={() => handleDelete(u.id)} disabled={deleting === u.id || u.id === user?.id} title={u.id === user?.id ? "Can't delete your own account" : "Remove user"} style={{ background: "none", border: "none", color: u.id === user?.id ? "#ddd" : "#ccc", fontSize: 16, cursor: u.id === user?.id ? "default" : "pointer", padding: "2px 4px" }}
                        onMouseEnter={e => { if (u.id !== user?.id) e.currentTarget.style.color = "#c62828"; }}
                        onMouseLeave={e => { e.currentTarget.style.color = u.id === user?.id ? "#ddd" : "#ccc"; }}>
                        {deleting === u.id ? "…" : "✕"}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f4f5f7",
      fontFamily: "Arial, sans-serif",
    }}>

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: "linear-gradient(160deg, #12285a 0%, #1a3a6e 55%, #1e4481 100%)",
        padding: "0 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 68,
        boxShadow: "0 3px 16px rgba(0,0,0,0.22)",
        position: "relative",
      }}>
        {/* Bottom accent stripe */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 3,
          background: "linear-gradient(90deg, #2563eb 0%, #38bdf8 40%, #1D9E75 100%)",
          opacity: 0.7,
        }} />

        {/* Left: logo + divider + app name */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img src="/logo.png" alt="DHIT" style={{ height: 38, objectFit: "contain" }}
               onError={e => { e.target.style.display = "none"; }} />
          <div style={{ width: 1, height: 30, background: "rgba(255,255,255,0.18)", flexShrink: 0 }} />
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 16, letterSpacing: "-0.2px", lineHeight: 1.2 }}>
              Agreement Builder
            </div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, letterSpacing: "0.03em", marginTop: 2 }}>
              Dynamic Health IT, Inc.
            </div>
          </div>
        </div>

        {/* Right: New Agreement + Test Panel (admin) + divider + user + sign out */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={onCreateNew}
            style={{
              background: "#1D9E75", color: "#fff", border: "none",
              padding: "8px 18px", borderRadius: 7, fontSize: 13,
              fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 7,
              boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
              letterSpacing: "-0.1px",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#18876a"}
            onMouseLeave={e => e.currentTarget.style.background = "#1D9E75"}
          >
            <span style={{ fontSize: 17, lineHeight: 1, marginTop: -1 }}>+</span>
            New Agreement
          </button>

          {user?.role === "admin" && (
            <>
              <button
                onClick={onTestPanel}
                title="Test Panel"
                style={{
                  background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)",
                  color: "rgba(255,255,255,0.7)", padding: "7px 12px",
                  borderRadius: 7, fontSize: 13, cursor: "pointer",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.14)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
              >
                🧪 Test
              </button>
              <button
                onClick={onChangelog}
                title="Changelog"
                style={{
                  background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)",
                  color: "rgba(255,255,255,0.7)", padding: "7px 12px",
                  borderRadius: 7, fontSize: 13, cursor: "pointer",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.14)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
              >
                📋 Changelog
              </button>
            </>
          )}

          <div style={{ width: 1, height: 28, background: "rgba(255,255,255,0.16)", margin: "0 4px" }} />

          {/* User avatar + name */}
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: "rgba(255,255,255,0.14)",
              border: "1.5px solid rgba(255,255,255,0.28)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 14, fontWeight: 700, flexShrink: 0,
              letterSpacing: "-0.5px",
            }}>
              {user?.name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?"}
            </div>
            <span style={{ color: "rgba(255,255,255,0.82)", fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" }}>
              {user?.name}
            </span>
          </div>

          <button
            onClick={async () => {
              await fetch("/api/auth/logout", { method: "POST" });
              window.location.reload();
            }}
            style={{
              background: "transparent", border: "1px solid rgba(255,255,255,0.22)",
              color: "rgba(255,255,255,0.72)", padding: "7px 14px",
              borderRadius: 7, fontSize: 12, cursor: "pointer",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.72)"; }}
          >
            Sign out
          </button>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 20px" }}>

        {/* ── Page header ───────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1a3a6e", marginBottom: 2 }}>
            Agreements
          </div>
          <div style={{ fontSize: 13, color: "#888" }}>
            {loading ? "Loading…" : `${drafts.length} agreement${drafts.length !== 1 ? "s" : ""} total`}
          </div>
        </div>

        {/* ── Stats row ─────────────────────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 28 }}>
          {stats.map(s => (
            <div key={s.label} style={{
              background: "#fff", borderRadius: 10, padding: "16px 20px",
              border: "0.5px solid #eee", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1, marginBottom: 4 }}>
                {s.value}
              </div>
              <div style={{ fontSize: 12, color: "#888" }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Error state ───────────────────────────────────────────────────── */}
        {error && (
          <div style={{ background: "#fdecea", color: "#c62828", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13 }}>
            Could not load agreements — {error}
          </div>
        )}

        {/* ── Loading state ─────────────────────────────────────────────────── */}
        {loading && (
          <div style={{ textAlign: "center", padding: 60, color: "#aaa", fontSize: 14 }}>
            Loading agreements…
          </div>
        )}

        {/* ── Pending section ───────────────────────────────────────────────── */}
        {!loading && pending.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8, marginBottom: 10
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%", background: "#F9A825", display: "inline-block"
              }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#795548" }}>
                Needs attention — {pending.length} pending approval
              </span>
            </div>
            <div style={{
              background: "#fff", borderRadius: 10,
              border: "1.5px solid #F9A825",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(249,168,37,0.1)",
            }}>
              {/* Table header */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 185px 120px 90px 150px 36px",
                gap: 12, padding: "10px 20px",
                background: "#FFFBF0", borderBottom: "0.5px solid #F9A825",
              }}>
                {["Agreement", "Status", "Created by", "Updated", "", ""].map(h => (
                  <div key={h} style={{ fontSize: 11, fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</div>
                ))}
              </div>
              {pending.map(d => <DraftRow key={d.token} draft={d} highlight={true} />)}
            </div>
          </div>
        )}

        {/* ── All agreements ────────────────────────────────────────────────── */}
        {!loading && (
          <div>
            {/* Header row: label + search */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                {pending.length > 0 ? "All other agreements" : "All agreements"}
                {q && <span style={{ fontWeight: 400, color: "#aaa", marginLeft: 8 }}>
                  {filteredOthers.length} result{filteredOthers.length !== 1 ? "s" : ""}
                </span>}
              </div>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#bbb", fontSize: 14, pointerEvents: "none" }}>
                  &#128269;
                </span>
                <input
                  type="text"
                  placeholder="Search by name, product, status…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(0); }}
                  style={{
                    paddingLeft: 32, paddingRight: 12, paddingTop: 7, paddingBottom: 7,
                    fontSize: 13, border: "1px solid #dde", borderRadius: 7,
                    outline: "none", width: 260, color: "#333",
                    background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}
                />
                {search && (
                  <button onClick={() => { setSearch(""); setPage(0); }} style={{
                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", color: "#bbb", fontSize: 14, padding: 0, lineHeight: 1,
                  }}>✕</button>
                )}
              </div>
            </div>

            {others.length === 0 && pending.length === 0 ? (
              <div style={{
                background: "#fff", borderRadius: 10, border: "0.5px solid #eee",
                padding: "48px 24px", textAlign: "center",
              }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#1a3a6e", marginBottom: 6 }}>No agreements yet</div>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>Create your first agreement to get started.</div>
                <button onClick={onCreateNew} style={{
                  background: "#1a3a6e", color: "#fff", border: "none",
                  padding: "10px 24px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer",
                }}>
                  + New Agreement
                </button>
              </div>
            ) : filteredOthers.length === 0 ? (
              <div style={{
                background: "#fff", borderRadius: 10, border: "0.5px solid #eee",
                padding: "32px 24px", textAlign: "center", color: "#aaa", fontSize: 13,
              }}>
                No agreements match &ldquo;{search}&rdquo;
              </div>
            ) : (
              <>
                <div style={{
                  background: "#fff", borderRadius: 10,
                  border: "0.5px solid #eee", overflow: "hidden",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}>
                  <div style={{
                    display: "grid", gridTemplateColumns: "1fr 185px 120px 90px 150px 36px",
                    gap: 12, padding: "10px 20px",
                    background: "#f9f9fb", borderBottom: "0.5px solid #eee",
                  }}>
                    {["Agreement", "Status", "Created by", "Updated", "", ""].map((h, i) => (
                      <div key={i} style={{ fontSize: 11, fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</div>
                    ))}
                  </div>
                  {pagedOthers.map(d => <DraftRow key={d.token} draft={d} highlight={false} />)}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, padding: "0 2px" }}>
                    <div style={{ fontSize: 12, color: "#aaa" }}>
                      Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredOthers.length)} of {filteredOthers.length}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        style={{
                          padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500,
                          border: "1px solid #dde", background: page === 0 ? "#f9f9fb" : "#fff",
                          color: page === 0 ? "#ccc" : "#555", cursor: page === 0 ? "default" : "pointer",
                        }}
                      >
                        ← Prev
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => (
                        <button
                          key={i}
                          onClick={() => setPage(i)}
                          style={{
                            padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 500,
                            border: "1px solid #dde",
                            background: i === page ? "#1a3a6e" : "#fff",
                            color: i === page ? "#fff" : "#555",
                            cursor: "pointer",
                            display: totalPages > 7 && Math.abs(i - page) > 2 && i !== 0 && i !== totalPages - 1 ? "none" : "inline-block",
                          }}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={page === totalPages - 1}
                        style={{
                          padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500,
                          border: "1px solid #dde", background: page === totalPages - 1 ? "#f9f9fb" : "#fff",
                          color: page === totalPages - 1 ? "#ccc" : "#555", cursor: page === totalPages - 1 ? "default" : "pointer",
                        }}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Team members (admin only) ──────────────────────────────────── */}
        {user?.role === "admin" && <TeamSection />}
        {user?.role === "admin" && <CountersignSection />}

      </div>
    </div>
  );
}
